import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Cpu, Gauge, ArrowRight, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AnimateOnScroll } from '../hooks/useScrollAnimation';

const MAX_FPS = 600;

/**
 * MetricRow — width is driven directly via refs (transform: scaleX) at 60fps
 * to avoid React re-renders fighting CSS transitions. The number is also
 * updated via ref to keep the whole row off the React reconciliation path.
 */
const MetricRow = React.forwardRef(({ label }, ref) => {
  const barRef = useRef(null);
  const valueRef = useRef(null);

  // Expose imperative update API to parent (RAF loop)
  React.useImperativeHandle(ref, () => ({
    update: (fps) => {
      const ratio = Math.max(0, Math.min(fps / MAX_FPS, 1));
      if (barRef.current) {
        // GPU-accelerated transform (no layout)
        barRef.current.style.transform = `scaleX(${ratio})`;
      }
      if (valueRef.current) {
        valueRef.current.textContent = fps.toFixed(1);
      }
    },
  }), []);

  return (
    <div>
      <div className="flex items-end justify-between mb-1.5 md:mb-2">
        <span className="text-[11px] md:text-sm text-gray-300 font-medium tracking-wide">{label}</span>
        <span className="font-mono tabular-nums">
          <span ref={valueRef} className="text-xl md:text-3xl font-bold text-white">0.0</span>
          <span className="text-[10px] md:text-xs text-gray-500 ml-1 font-normal">FPS</span>
        </span>
      </div>
      <div className="relative h-2 md:h-2.5 bg-white/[0.04] rounded-full overflow-hidden">
        <div
          ref={barRef}
          className="absolute inset-y-0 left-0 right-0 bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full"
          style={{
            transform: 'scaleX(0)',
            transformOrigin: 'left center',
            willChange: 'transform',
            boxShadow: '0 0 14px rgba(8, 145, 178, 0.45)',
          }}
        />
      </div>
    </div>
  );
});
MetricRow.displayName = 'MetricRow';

/**
 * Compact benchmark teaser shown on the homepage.
 * Mirrors the visuals of BenchmarkPage with imperative ref-driven animation
 * for silky 60fps without re-renders.
 */
const BenchmarkPreview = () => {
  const navigate = useNavigate();

  const benchmark = useMemo(() => ({
    cpu: 'AMD Ryzen 7 5800X',
    gpu: 'NVIDIA RTX 3060',
    before: { average: 320.6, low1: 135.8, low02: 112.5 },
    after:  { average: 549.5, low1: 275.1, low02: 178.0 },
  }), []);

  // isOptimized triggers a re-render only for UI (toggle styling, gain chips, etc.)
  const [isOptimized, setIsOptimized] = useState(false);

  const gains = useMemo(() => {
    const b = benchmark.before;
    const a = benchmark.after;
    const pct = (x, y) => Math.round(((y - x) / x) * 1000) / 10;
    return {
      average: pct(b.average, a.average),
      low1:    pct(b.low1,    a.low1),
      low02:   pct(b.low02,   a.low02),
    };
  }, [benchmark]);

  // Refs to each row's imperative update API
  const rowAvgRef = useRef(null);
  const rowLow1Ref = useRef(null);
  const rowLow02Ref = useRef(null);
  const sectionRef = useRef(null);

  // Animation state held outside React
  const animRef = useRef({
    current: { average: 320.6, low1: 135.8, low02: 112.5 },
    target:  { average: 320.6, low1: 135.8, low02: 112.5 },
    nextRetarget: 0,
    mode: 'before',
    rafId: 0,
    visible: true,
    lastTs: 0,
  });

  const pickTarget = useCallback((mode) => {
    const base = mode === 'after' ? benchmark.after : benchmark.before;
    if (mode === 'after') {
      // AFTER: visible but tight micro-fluctuations (~±2-3 FPS) so the bar feels alive
      const j = (a) => (Math.random() - 0.5) * a;
      return {
        average: base.average + j(6.0),   // ±3 FPS
        low1:    base.low1    + j(5.0),   // ±2.5 FPS
        low02:   base.low02   + j(4.0),   // ±2 FPS
      };
    }
    const r = Math.random();
    const dip = r < 0.06 ? (30 + Math.random() * 20)
              : r < 0.22 ? (12 + Math.random() * 14)
              : 0;
    const j = (a) => (Math.random() - 0.5) * a;
    return {
      average: base.average + j(14) - dip,
      low1:    base.low1    + j(10) - dip * 0.85,
      low02:   base.low02   + j(7)  - dip * 0.7,
    };
  }, [benchmark]);

  // Switch mode without restarting the animation
  useEffect(() => {
    const s = animRef.current;
    s.mode = isOptimized ? 'after' : 'before';
    s.nextRetarget = performance.now(); // force immediate retarget for snappy toggle
  }, [isOptimized]);

  // Single RAF loop that lives for the whole component lifecycle
  useEffect(() => {
    const s = animRef.current;
    s.lastTs = performance.now();

    const frame = (now) => {
      const dt = Math.min((now - s.lastTs) / 1000, 0.1);
      s.lastTs = now;

      // Skip work if tab/section is hidden
      if (!s.visible) {
        s.rafId = requestAnimationFrame(frame);
        return;
      }

      const mode = s.mode;
      if (now >= s.nextRetarget) {
        s.target = pickTarget(mode);
        // Shorter retarget interval in 'after' so the bar visibly drifts continuously
        const interval = mode === 'after' ? (320 + Math.random() * 260) : (380 + Math.random() * 280);
        s.nextRetarget = now + interval;
      }

      // Critically damped-ish smoothing (frame-rate independent)
      const k = mode === 'after' ? 3.6 : 4.2;
      const a = 1 - Math.exp(-k * dt);

      s.current.average += (s.target.average - s.current.average) * a;
      s.current.low1    += (s.target.low1    - s.current.low1)    * a;
      s.current.low02   += (s.target.low02   - s.current.low02)   * a;

      if (s.current.average < 0) s.current.average = 0;
      if (s.current.low1   < 0) s.current.low1   = 0;
      if (s.current.low02  < 0) s.current.low02  = 0;

      // Direct DOM writes — no React re-render
      rowAvgRef.current?.update(s.current.average);
      rowLow1Ref.current?.update(s.current.low1);
      rowLow02Ref.current?.update(s.current.low02);

      s.rafId = requestAnimationFrame(frame);
    };

    s.rafId = requestAnimationFrame(frame);

    // Pause animation when not on screen (perf + battery)
    let tabVisible = !document.hidden;
    let onScreen = true;
    const applyVisible = () => { s.visible = tabVisible && onScreen; };
    const handleVisibility = () => { tabVisible = !document.hidden; applyVisible(); };
    document.addEventListener('visibilitychange', handleVisibility);

    let observer;
    if (sectionRef.current && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        onScreen = entries[0].isIntersecting;
        applyVisible();
      }, { threshold: 0 });
      observer.observe(sectionRef.current);
    }
    applyVisible();

    return () => {
      cancelAnimationFrame(s.rafId);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (observer) observer.disconnect();
    };
  }, [pickTarget]);

  return (
    <section ref={sectionRef} className="relative py-12 md:py-20 px-4 md:px-8 overflow-hidden" data-testid="home-benchmark-preview">
      {/* Subtle cyan radial accent */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[radial-gradient(circle_at_center,rgba(8,145,178,0.08),transparent_60%)]" />
      </div>

      <div className="relative max-w-5xl mx-auto">
        <AnimateOnScroll animation="fade-down" className="text-center mb-6 md:mb-10">
          <span className="inline-block px-3 md:px-4 py-1 md:py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-[10px] md:text-xs font-semibold uppercase tracking-[0.18em] mb-3 md:mb-5">
            Nos Benchmarks
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-3 tracking-tight">
            Avant / Après <span className="text-cyan-400">optimisation</span>
          </h2>
          <p className="text-gray-400 text-sm md:text-base max-w-2xl mx-auto px-4">
            Comparatif réel mesuré sur une configuration milieu de gamme — chiffres bruts, sans triche.
          </p>
        </AnimateOnScroll>

        <AnimateOnScroll animation="fade-up">
          <div className="relative bg-gradient-to-b from-zinc-900/70 to-zinc-950/80 border border-white/10 rounded-2xl md:rounded-3xl p-4 md:p-7 backdrop-blur-sm overflow-hidden">
            <div className="pointer-events-none absolute -top-32 -right-32 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl" />
            <div className="pointer-events-none absolute -bottom-32 -left-32 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl" />

            {/* Toggle */}
            <div className="relative flex justify-center mb-5 md:mb-7">
              <div className="inline-flex items-center p-1 rounded-full bg-black/40 border border-white/10 backdrop-blur">
                <button
                  onClick={() => setIsOptimized(false)}
                  data-testid="home-benchmark-toggle-avant"
                  className={`relative px-5 md:px-7 py-2 rounded-full text-xs md:text-sm font-semibold transition-colors duration-300 ${
                    !isOptimized ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                  }`}
                >
                  {!isOptimized && (
                    <span className="absolute inset-0 rounded-full bg-white/10 shadow-[0_0_0_1px_rgba(255,255,255,0.1)_inset]" />
                  )}
                  <span className="relative">Avant</span>
                </button>
                <button
                  onClick={() => setIsOptimized(true)}
                  data-testid="home-benchmark-toggle-apres"
                  className={`relative px-5 md:px-7 py-2 rounded-full text-xs md:text-sm font-semibold transition-colors duration-300 ${
                    isOptimized ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                  }`}
                >
                  {isOptimized && (
                    <span className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 shadow-[0_0_30px_-6px_rgba(6,182,212,0.6)]" />
                  )}
                  <span className="relative">Après</span>
                </button>
              </div>
            </div>

            {/* Hardware strip */}
            <div className="relative flex flex-wrap items-center justify-center gap-2 mb-5 md:mb-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-[11px] md:text-sm">
                <Cpu className="w-3.5 h-3.5 text-green-400" />
                <span className="text-gray-200">{benchmark.cpu}</span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-[11px] md:text-sm">
                <Gauge className="w-3.5 h-3.5 text-cyan-400" />
                <span className="text-gray-200">{benchmark.gpu}</span>
              </div>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] md:text-sm transition-all duration-500 ${
                isOptimized
                  ? 'bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 opacity-100'
                  : 'bg-white/[0.02] border border-white/5 text-gray-500 opacity-60'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${isOptimized ? 'bg-cyan-400 animate-pulse' : 'bg-gray-600'}`} />
                {isOptimized ? 'Optimisé par AqmOpti' : 'Windows Stock'}
              </div>
            </div>

            {/* FPS rows (ref-driven, no re-renders) */}
            <div className="relative space-y-5 md:space-y-7">
              <MetricRow ref={rowAvgRef}   label="Average FPS" />
              <MetricRow ref={rowLow1Ref}  label="1% Low FPS" />
              <MetricRow ref={rowLow02Ref} label="0.2% Low FPS" />
            </div>

            {/* Gain chips */}
            {isOptimized && (
              <div className="relative grid grid-cols-3 gap-2 md:gap-3 mt-6 md:mt-8 animate-fadeIn">
                {[
                  { label: 'Avg FPS',  pct: gains.average },
                  { label: '1% Low',   pct: gains.low1 },
                  { label: '0.2% Low', pct: gains.low02 },
                ].map((g, i) => (
                  <div key={i} className="bg-white/[0.02] border border-white/10 rounded-xl p-3 md:p-4 text-center">
                    <div className="text-lg md:text-2xl font-bold text-cyan-400 tabular-nums font-mono leading-none">+{g.pct}%</div>
                    <div className="text-[9px] md:text-[11px] text-gray-500 mt-1.5 uppercase tracking-wider">{g.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* CTA */}
            <div className="relative mt-6 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <p className="text-[11px] md:text-xs text-gray-500 text-center sm:text-left">
                🎮 Tests en partie BR, mode replay — les gains varient selon la configuration.
              </p>
              <button
                onClick={() => navigate('/benchmark')}
                data-testid="home-benchmark-cta"
                className="group inline-flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 rounded-full bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 hover:border-cyan-400/50 text-cyan-200 hover:text-white text-xs md:text-sm font-semibold transition-all whitespace-nowrap"
              >
                <TrendingUp className="w-3.5 h-3.5 md:w-4 md:h-4" />
                Voir tous les benchmarks
                <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default BenchmarkPreview;
